import React from 'react';
import { Card, Table, Pager, EmptyState, ErrorState, SkeletonText, FilterChip } from '@parsina/framework';
import { Receipt } from 'lucide-react';

import { C, CT } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { fmtParseh } from '../../constants/pricing';
import { DEFAULT_PAGE_SIZE } from '../../lib/paging.js';
import { TX_FILTERS, serviceLabelKey, transactionTitle } from './Credits.shared.js';

// تراکنش‌ها — the پارسه ledger.
//
// ── The filter is SERVER-side, and that is not an optimisation ──────────
//
// `myWalletTransactions.totalCount` is "the size of the whole FILTERED set, not
// of this page". A client-side filter over an already-paged list would page
// against a total that counts rows it is hiding — the pager would offer pages
// that render empty, and the count under the table would describe a different
// list from the one on screen.
//
// ── The amount is a COUNT, never money ──────────────────────────────────
//
// پارسه is not a currency: it has no minor unit, no grouping convention of its
// own, and `accounting`'s money helpers divide by ten because THEIR numbers are
// rials. `fmtParseh` rounds and localises the digits, and that is the whole of
// it.

export default function TransactionList({ ledger, filter, onFilter, page, onPage, compact = false }) {
  const { t, formatNumber, formatDate } = useTranslation();

  const columns = [
    {
      key: 'date',
      header: t('credits.tx.columns.date'),
      width: 130,
      render: (row) => (
        <span style={{ fontSize: 12.5, color: C.textMuted }}>{formatDate(row.createdAt, 'numeric')}</span>
      ),
    },
    {
      key: 'service',
      header: t('credits.tx.columns.service'),
      render: (row) => (
        <span style={{ fontWeight: 700, color: C.textPrimary }}>{t(serviceLabelKey(row.serviceType))}</span>
      ),
    },
    {
      key: 'description',
      header: t('credits.tx.columns.description'),
      render: (row) => {
        // The wallet's own description, when it carries a readable one. Token
        // counts and the model name are stripped by `transactionTitle` — those
        // are the platform's unit economics, not the student's.
        const title = transactionTitle(row.description);
        return (
          <span style={{ fontSize: 12.5, color: CT.ink2 }}>
            {title || t('common.labels.none')}
          </span>
        );
      },
    },
    {
      key: 'amount',
      header: t('credits.tx.columns.amount'),
      width: 110,
      align: 'end',
      render: (row) => {
        const debit = row.type === 'debit';
        return (
          <span
            style={{ fontWeight: 800, color: debit ? C.textSecondary : C.green }}
            dir="ltr"
          >
            <span style={{ unicodeBidi: 'isolate' }}>
              {debit ? '−' : '+'}
              {fmtParseh(row.amount, formatNumber)}
            </span>
          </span>
        );
      },
    },
  ];

  return (
    <Card style={{ padding: 0, overflow: 'hidden' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
          padding: '12px 14px',
          borderBottom: `1px solid ${C.border}`,
        }}
      >
        <span style={{ fontSize: 13.5, fontWeight: 800, color: C.textPrimary }}>
          {t('credits.tx.title')}
        </span>
        <div style={{ display: 'flex', gap: 6, marginInlineStart: 'auto', flexWrap: 'wrap' }}>
          {TX_FILTERS.map(({ id, value, labelKey }) => (
            <FilterChip
              key={id}
              label={t(labelKey)}
              active={(filter || null) === value}
              onClick={() => onFilter(value)}
            />
          ))}
        </div>
      </div>

      {ledger.error ? (
        <ErrorState
          title={t('credits.tx.error.title')}
          onRetry={ledger.reload}
          retryLabel={t('common.actions.retry')}
        />
      ) : ledger.loading ? (
        <div style={{ padding: 20 }}>
          <SkeletonText lines={compact ? 4 : 8} />
        </div>
      ) : (
        <>
          <Table
            columns={compact ? columns.filter((c) => c.key !== 'description') : columns}
            rows={ledger.transactions}
            rowKey={(row) => row.id}
            dense={compact}
            empty={
              <EmptyState
                icon={<Receipt size={40} color={C.textMuted} />}
                title={t('credits.tx.empty.title')}
                description={t('credits.tx.empty.body')}
              />
            }
          />
          {/* `totalCount` is the FILTERED total, which is exactly what the pager
              needs — and the reason the filter is not applied in the browser. */}
          <Pager
            page={page}
            total={ledger.totalCount}
            pageSize={DEFAULT_PAGE_SIZE}
            onChange={onPage}
          />
        </>
      )}
    </Card>
  );
}
